<template>
  <div
    class="product-page"
  >
    <div class="p-8 desktop">
      <h1
        class="h1--underline product-page__headline"
        v-if="product !== null"
        v-html="getProductText"
      />
      <span
        v-if="product !== null"
        class="h5 product-page__headline"
        v-html="productDescription"
      />
    </div>
    <CategoryNavigation
      class="mobile"
      :menu-items="productPageNavs"
      :default-menu-item-id="'product-bar__details'"
      :offset="loggedIn ? offsetLoggedIn : offsetLoggedOut"
    />
    <div
      class="app-vue"
    >
      <div
        class="product-bar two-column"
        id="product-bar"
      >
        <div :class="COLUMN_ONE">
          <ProductDetails
            ref="pdpPageProductDetails"
            id="product-bar__details"
            class="pdp-box product-bar__details"
            :product="productWithCategoryDesigns"
            :brand="brand"
            :download-files="productDownloads"
            :product-name="productDescription"
            @open-variants="showVariants = true"
            @keydown.tab="handleTabOnProductDetails"
          />
          <DimensionsAndInstallation
            ref="pdpPageDimensionsAndInstallation"
            :id="DIMENSION_ID"
            class="pdp-box product-bar__dimensions"
            :product="productWithCategoryDesigns"
            :product-attributes="productAttributes"
            :dimensions="productDimensionsFiltered"
            :product-downloads="productDownloads"
            @keydown.tab="handleTabOnDimensionsInstallation"
          />
        </div>
        <div
          ref="pdpColumnTwo"
          :class="COLUMN_TWO"
        >
          <div
            :id="AVAILABILITY_PRICING_ID"
            class="product-bar__availability"
          >
            <AvailabilityPricing
              ref="pdpPageAvailabilityPricingDesktop"
              :product="buyboxProduct"
              :loading="loadingPDP"
              @addToCart="handleAddToCart"
              :add-in-progress="addInProgress"
              @keydown.tab="handleTabOnAvailabilityPricing"
            />
          </div>
        </div>
      </div>
      <PopupSuccessorVariants
        :show="showVariants"
        :sku-array="variantsAndSuccessor"
        @close-variants="showVariants = false"
      />
    </div>
    <product-json-ld
      v-if="product"
      :product="product"
      :brand="brand"
      :category="categoryContents && categoryContents.Name"
    />
  </div>
</template>

<script setup>
import {useProductService} from '@/composables/product_service'
import {storeToRefs} from 'pinia'
import ProductJsonLd from '@/components/product-detail/ProductJSONLD'
import {sanitizeHtml} from '~/assets/js/utils'
import {getMappedProductAttributesNew, getMappedProductCategoryImagesNew, getMappedProductNew} from '~/assets/js/utils/product_attributes_mapping'
import {PROPERTY_SCOPE_NAMES, FILENAME_REGEX, ADDITEM_STATUS, BREAKPOINTS, TELEPORT_NAMES, CATEGORIES} from '~/assets/js/constants'
import {USER_RIGHTS_MAPPING} from '~/assets/js/constants.js'
import {useUserStore} from '@/store/user'
import {useCartStore} from '@/store/cart'
import ProductDetails from '~/components/product-detail/bb_components/ProductDetails'
import DimensionsAndInstallation from '~/components/product-detail/bb_components/DimensionsAndInstallation'
import AvailabilityPricing from '~/components/product-detail/bb_components/AvailabilityPricing'
import PopupSuccessorVariants from '~/components/popup/PopupSuccessorVariants.vue'
import useI18nExtended from '~/composables/usei18nExtended'
import {useRightsHelper} from '~/composables/rights_helper'
import {useGtm} from '@gtm-support/vue-gtm'
import {useCanonicalPathHelper} from '~/composables/canonical_path_helper'
import {usePimHelper} from '~/composables/pim_helper'
import useBreadcrumb from '@/composables/useBreadcrumb'
import useGrid from '~/composables/useGrid'
import {useBrand} from '~/composables/useBrand.ts'
import CategoryNavigation from '~/components/category/CategoryNavigation.vue'

const emit = defineEmits(['productSuccessfullyAdded'])

const {getIndexPathValue} = useGrid()

const {getProductBySkuNew} = useProductService()

const {
  userHasRight
} = useRightsHelper()

const {
  loadProductCategoryWithPim
} = usePimHelper()

const {getBrandByCode} = useBrand()

const route = useRoute()
const router = useRouter()
const params = route.params
const i18n = useI18nExtended()
const region = useNuxtApp().$globalization?.getRegion()
const nuxtApp = useNuxtApp()
const {showInchValues, loadedCustomerUuid} = storeToRefs(useUserStore())
const {deliveryAddressUuid} = storeToRefs(useCartStore())
const {addItems} = useCartStore()
const {adjustCanonicalPath} = useCanonicalPathHelper()

const product = ref(null)
let categoryContents = null
let brand = null
let productSku = params.product
const offsetLoggedIn = ref(130)
const offsetLoggedOut = ref(105)
const productLoadedInSetup = ref(false)
let reloadingProduct = ref(false)
let loadingPDP = ref(true)
let showVariants = ref(false)
let addInProgress = ref(false)
let pdpPageAvailabilityPricingDesktop = ref(null)
let pdpColumnTwo = ref(null)
let pdpPageProductDetails = ref(null)
let pdpPageDimensionsAndInstallation = ref(null)
const productPageNavs = ref([
  {id: 'product-bar__details', label: i18n.tv('details', 'product')},
  {id: 'product-availabilityPricing', label: i18n.tv('availabilityPricing', 'product')},
  {id: 'product-bar__dimensions', label: i18n.tv('dimensionsInstallation', 'product')}
])

if (!product.value) {
  try {
    product.value = await getProductBySkuNew(productSku, region, i18n.locale.value, showInchValues.value, null, PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW)
    productLoadedInSetup.value = true
  } catch (e) {
    throw showError({statusCode: 404, message: `Product not found 1 ${productSku}`})
  }

  if (!product.value) {
    throw showError({statusCode: 404, message: `Product not found ${productSku}`})
  }
} else {
  if (!product.value) {
    throw showError({statusCode: 404, message: `Product payload not found ${productSku}`})
  }
}

try {
  brand = getBrandByCode(product.value?.brandName[0], i18n.locale.value)
} catch (e) {
  // eslint-disable-next-line no-console
  console.error(e)
}

let categoryJson = await loadProductCategoryWithPim(product?.value?.productDesign, route)

let translatedParams = {}
for (let localeCode of i18n.localeCodes.value) {
  translatedParams[localeCode] = {
    design: categoryJson?.url_alternatives?.[region?.toLowerCase()]?.[localeCode]?.split('/')?.at(-1) || product?.value?.productDesign,
    product: params.product
  }
}
route.meta.nuxtI18n = translatedParams

let breadcrumbArray = []
if (categoryJson) {
  breadcrumbArray.push({
    label: i18n.te('breadcrumbs.category', i18n.locale.value) ? i18n.tv('category', 'breadcrumbs') : 'category',
    path: `${CATEGORIES.RootCategoriesUrl}`
  })
  breadcrumbArray = breadcrumbArray
    .concat(categoryJson?.breadcrumb?.map(breadcrumb => {
      return {
        label: breadcrumb.name,
        path: breadcrumb.url
      }}))
}

breadcrumbArray.push({
  label: params.product,
  path: `${categoryJson?.url}/${i18n.tv('products', 'pages')}/${encodeURIComponent(params.product)}`
})
useBreadcrumb(breadcrumbArray)

//head
const path = route.path
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value
adjustCanonicalPath(path, i18nHead)

const lang = i18n.locale.value.toUpperCase()

const productDescription = product.value[`locales_${lang}_SalesText`]

const unit = showInchValues.value ? 'Imperial' : 'Metric'
const titleKey = `locales_${lang}_${unit}_SalesTextECom`

const rawTitle = titleKey && titleKey in product.value ? product.value[titleKey] : null
const title = rawTitle
  ? `${product.value?.sku} | ${sanitizeHtml(rawTitle)}`
  : `Products | Freudenberg FST | ${product.value?.sku}`

const head = {
  title,
  categoryLanguage: i18n.locale.value,
  htmlAttrs: {
    ...i18nHead.htmlAttrs
  },
  meta: [
    ...i18nHead.meta,
    {
      hid: 'description',
      name: 'description',
      content: categoryJson?.SEO?.meta_description ?? ''
    },
    {
      name: 'robots',
      content: 'all'
    }
  ],
  link: [...i18nHead.link]
}

useHead(head)

const COLUMN_ONE = computed(() => {
  return TELEPORT_NAMES.COLUMN_ONE
})
const COLUMN_TWO = computed(() => {
  return TELEPORT_NAMES.COLUMN_TWO
})
const DIMENSION_ID = computed(() => {
  return TELEPORT_NAMES.DIMENSION_ID
})
const AVAILABILITY_PRICING_ID = computed(() => {
  return TELEPORT_NAMES.AVAILABILITY_PRICING_ID
})
const variantsAndSuccessor = computed(() => {
  const variants = (nuxtApp.$deproxy(product?.value?.otherVariants) ?? []).map(v => ({...v, isSuccessor: false}))
  const successor = product?.value?.salesAreas.map(s => {
    // eslint-disable-next-line eqeqeq
    return s.successorSku != null ? {sku: s.successorSku, isSuccessor: true} : null
  })
  let result = null
  if (successor && variants) {
    result = [{sku: product?.value?.sku}, ...successor, ...variants]
  }
  else if (successor) {
    result = [{sku: product?.value?.sku}, ...successor]
  }
  else if (variants) {
    result = [{sku: product?.value?.sku}, ...variants]
  }
  else {
    result = [{sku: product?.value?.sku}]
  }
  // eslint-disable-next-line eqeqeq
  return result.filter(vs => vs != null)
})
const productDimensionsFiltered = computed(() => {
  if (!categoryJson?.categoryAttributes_detail_dimension || categoryJson?.categoryAttributes_detail_dimension?.length === 0) {
    return []
  }

  let returnDimensions = []
  let dimensionAttributes = getMappedProductAttributesNew(categoryJson.categoryAttributes_detail_dimension, product.value)
  dimensionAttributes?.forEach(pa => {
    const shortName = pa.shortName
    const shortLabel = shortName !== 'shortNameNotAvailable' && shortName !== '' ? ' (' + shortName + ')' : ''
    const translatedProductAttribute = pa.displayName
    let value = getIndexPathValue(pa.indexPath, product.value)
    if (pa.unitOfMeasure) {
      const lowerUnitOfMeasure = pa.unitOfMeasure.toLowerCase()
      const translatedUnitOfMeasure = showInchValues.value && lowerUnitOfMeasure === 'mm'
        ? i18n.tv('inch', 'unitsOfMeasure')
        : i18n.tv(lowerUnitOfMeasure, 'unitsOfMeasure')
      value += ' ' + translatedUnitOfMeasure
    }
    returnDimensions.push({
      technicalName: pa.technicalName,
      label: translatedProductAttribute !== shortName ? translatedProductAttribute + shortLabel : translatedProductAttribute,
      value: i18n.tv(value, 'attributes.' + pa.technicalName + '-values')
    })
  })

  return returnDimensions
})
const productDownloads = computed(() => {
  let result = []

  // Get non-datasheet assets from product index (CAD, InstallationSketch, etc.)
  if (product.value?.assets) {
    const nonDatasheetAssets = product.value.assets.filter(asset => asset.assetType !== 'ProductDataSheet')
    result = nonDatasheetAssets.filter(asset => asset.languageShortCode === i18n.locale.value)
    let presentAssetTypes = result.map(asset => asset.assetType)
    result = result.concat(nonDatasheetAssets.filter(asset => asset.languageShortCode === i18n.defaultLocale && !presentAssetTypes.includes(asset.assetType)))
    result = result.map((asset) => ({
      ...asset,
      // alt text is to be set to the filename to identify materials in the download section, the filename is extracted from the lookupKey (#28924)
      altText: asset.label ||
        (asset.lookupKey && FILENAME_REGEX.test(asset.lookupKey) && asset.lookupKey.match(FILENAME_REGEX)?.groups?.filename
          ? decodeURI(asset.lookupKey.match(FILENAME_REGEX)?.groups?.filename)
          : null),
      sortOrder: getSortOrder(asset.assetType)
    }))
  }

  // Add datasheets from PIM (categoryJson)
  if (categoryJson?.contents) {
    const pimBaseUrl = useRuntimeConfig().public.pimDataBaseUrl || ''
    const pimDatasheets = categoryJson.contents
      .filter(c => c.type === 'download')
      .map((categoryAsset) => {
        let downloadUrl = categoryAsset.url
        if (downloadUrl && !downloadUrl.startsWith('http://') && !downloadUrl.startsWith('https://')) {
          downloadUrl = pimBaseUrl + (downloadUrl.startsWith('/') ? '' : '/') + downloadUrl
        }

        return {
          assetType: 'ProductDataSheet',
          lookupKey: downloadUrl,
          altText: categoryAsset.label,
          label: categoryAsset.label,
          languageShortCode: categoryAsset.language || i18n.locale.value,
          sortOrder: getSortOrder('ProductDataSheet'),
          sourceCMS: true // Mark as coming from PIM/CMS
        }
      })
    result = result.concat(pimDatasheets)
  }

  return result?.length > 0 ? result.sort((a, b) => a.sortOrder - b.sortOrder) : result
})
const productWithCategoryDesigns = computed(() => {
  return getMappedProductCategoryImagesNew(getMappedProductNew(product.value), categoryJson)
})
const productAttributes = computed(() => {
  if (!categoryJson?.categoryAttributes_detail_attribute || categoryJson?.categoryAttributes_detail_attribute?.length === 0) {
    return []
  }

  const attributes = []
  categoryJson.categoryAttributes_detail_attribute?.forEach(ca => {
    const shortName = ca.shortName
    const shortLabel = shortName !== 'shortNameNotAvailable' ? ' (' + shortName + ')' : ''
    const identifier = ca.displayName + shortLabel
    const value = getIndexPathValue(ca.indexPath, product.value)
    attributes.push({
      attributeIdentifier: identifier,
      value: i18n.tv(value, 'attributes.' + ca.technicalName + '-values')
    })
  })

  return attributes
})
const loggedIn = computed(() => {
  return nuxtApp.$auth.loggedIn
})
const getProductText = computed(() => {
  if (!product.value) {
    return ''
  }
  const lang = i18n.locale.value.toUpperCase()
  const unit = showInchValues.value ? 'Imperial' : 'Metric'
  const key = 'locales_' + lang + '_' + unit + '_SalesTextECom'
  if (product?.value instanceof Object && key in product.value) {
    return product.value[key]
  } else {
    return ''
  }
})
const buyboxProduct = computed(() => {
  return getMappedProductNew(product.value)
})
const productDetailsFocusableElements = computed(() => {
  const allFocusableElements = pdpPageProductDetails?.value?.$el?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable]'
  )
  return allFocusableElements
    ? Array.from(allFocusableElements).filter(element => element.offsetParent !== null)
    : []
})
const availabilityPricingFocusableElements = computed(() => {
  const allFocusableElements = pdpPageAvailabilityPricingDesktop?.value?.$el?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable]'
  )
  return allFocusableElements
    ? Array.from(allFocusableElements).filter(element => element.offsetParent !== null)
    : []
})
const dimensionsInstallationFocusableElements = computed(() => {
  const allFocusableElements = pdpPageDimensionsAndInstallation?.value?.$el?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable]'
  )
  return allFocusableElements
    ? Array.from(allFocusableElements).filter(element => element.offsetParent !== null)
    : []
})

const handleAddToCart = async (payload) => {
  addInProgress.value = true
  const markedAsRequest = payload?.markedAsRequest ?? false
  const quantity = payload?.quantity ?? 1
  const availabilityDate = payload?.availabilityDate ?? null
  const warehouse = payload?.warehouse ?? null

  if (!userHasRight(USER_RIGHTS_MAPPING.PRODUCT_BUY) && !userHasRight(USER_RIGHTS_MAPPING.PRODUCT_REQUEST)) {
    nuxtApp.$toast.error(i18n.tv('failed', 'cart'))
    addInProgress.value = false
  }

  try {
    let item = {
      sku: product?.value?.sku,
      quantity,
      availabilityDate
    }

    if (markedAsRequest) {
      item.markedAsRequest = markedAsRequest
    }

    if (warehouse && !warehouse.isDefault && warehouse.alternativePlantGuid) {
      item.alternativePlantUuid = warehouse.alternativePlantGuid
    }
    let res = await addItems({itemsToAdd: [item], region: nuxtApp.$globalization.getRegion(), locale: i18n.locale.value})
    if (res && res?.length > 0 && res[0].status === ADDITEM_STATUS.SUCCESS) {
      nuxtApp.$toast.success(useToastificationHelper().translateAndFormat(
        {messageKey: (markedAsRequest ? 'itemAddedWithQtyRequest' : 'itemAddedWithQtyBuy'), messageValues: {sku: product?.value?.sku, quantity: res[0]?.addedQty.toLocaleString(i18n.locale.value === 'en' ? 'en' : 'de')}}, 'cart'))
      emit('productSuccessfullyAdded')
    } else {
      if (res && res?.length > 0 && res[0].errors?.length > 0 && !res[0].errors.some(e => e.messageKey === 'Cart_Max_Items_Exceeded')) {
        nuxtApp.$toast.error(i18n.tv('failed', 'cart'))
      }
      addInProgress.value = false
      return
    }
  }
  catch (e) {
    nuxtApp.$toast.error(i18n.tv('failed', 'cart'))
    addInProgress.value = false
  }
  addInProgress.value = false
}
const handleTabOnProductDetails = (e) => {
  if (window.getComputedStyle(pdpColumnTwo.value).display === 'none') {
    return
  }
  if (!e.shiftKey && document.activeElement === productDetailsFocusableElements.value[productDetailsFocusableElements.value?.length - 1]) {
    if (availabilityPricingFocusableElements.value[0]) {
      e.preventDefault()
      availabilityPricingFocusableElements.value[0].focus()
    }
  }
}
const handleTabOnAvailabilityPricing = (e) => {
  if (window.getComputedStyle(pdpColumnTwo.value).display === 'none') {
    return
  }
  if (!e.shiftKey && document.activeElement === availabilityPricingFocusableElements.value[availabilityPricingFocusableElements.value?.length - 1]) {
    if (dimensionsInstallationFocusableElements.value[0]) {
      e.preventDefault()
      dimensionsInstallationFocusableElements.value[0].focus()
    }
  }
  if (e.shiftKey && document.activeElement === availabilityPricingFocusableElements.value[0]) {
    if (productDetailsFocusableElements.value[productDetailsFocusableElements.value?.length - 1]) {
      e.preventDefault()
      productDetailsFocusableElements.value[productDetailsFocusableElements.value?.length - 1].focus()
    }
  }
}
const handleTabOnDimensionsInstallation = (e) => {
  if (window.getComputedStyle(pdpColumnTwo.value).display === 'none') {
    return
  }
  if (!e.shiftKey && document.activeElement === dimensionsInstallationFocusableElements.value[dimensionsInstallationFocusableElements.value?.length - 1]) {
    const lastAPElement = availabilityPricingFocusableElements.value[availabilityPricingFocusableElements.value?.length - 1]
    if (lastAPElement) {
      e.preventDefault()
      lastAPElement.focus()
      lastAPElement.blur()
    }
  }
  if (e.shiftKey && document.activeElement === dimensionsInstallationFocusableElements.value[0]) {
    if (availabilityPricingFocusableElements.value[availabilityPricingFocusableElements.value?.length - 1]) {
      e.preventDefault()
      availabilityPricingFocusableElements.value[availabilityPricingFocusableElements.value?.length - 1].focus()
    }
  }
}

const getSortOrder = (assetType) => {
  // ProductDataSheet should appear first
  if (assetType === 'ProductDataSheet') {
    return 1
  }
  // CAD files second
  if (assetType === 'CAD') {
    return 2
  }
  // Everything else after
  return 0
}

const reloadProducts = () => {
  if (product.value) {
    loadingPDP.value = true
    let sku = product.value?.sku || productSku
    reloadingProduct.value = true
    nextTick(() => {
      const alternativePlantCode = product?.value?.alternativePlantCode ?? null
      const productUpdater = getProductBySkuNew(sku, nuxtApp.$globalization.getRegion(), i18n.locale.value, showInchValues.value, alternativePlantCode, PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW)
      productUpdater.then(p => product.value = p).catch(() => {
        product.value.buyable = false
        product.value.requestable = false
        brand = getBrandByCode(product.value?.brand[0], i18n.locale.value)
      }).finally(() => {
        reloadingProduct.value = false
        loadingPDP.value = false
        pdpPageAvailabilityPricingDesktop?.value?.updateData()
      })
    })
  }
}

const checkMobile = () => {
  let lowerBreakpoint = window.innerWidth < BREAKPOINTS.BETWEEN_MD_LG
  const availabilityPricingComponent = document.getElementById(TELEPORT_NAMES.AVAILABILITY_PRICING_ID)
  const column = lowerBreakpoint ? document.getElementsByClassName(TELEPORT_NAMES.COLUMN_ONE).item(0) : document.getElementsByClassName(TELEPORT_NAMES.COLUMN_TWO).item(0)
  if (lowerBreakpoint) {
    const dimensionComponent = document.getElementById(TELEPORT_NAMES.DIMENSION_ID)
    column?.insertBefore(availabilityPricingComponent, dimensionComponent)
  } else {
    column?.appendChild(availabilityPricingComponent)
  }
}

watch(deliveryAddressUuid, (newValue) => {
  if (newValue !== '') {
    reloadProducts()
  }
}, {immediate: false})

watch(loadedCustomerUuid, () => {
  reloadProducts()
}, {immediate: false})

watch(showInchValues, () => {
  reloadProducts()
}, {immediate: false})

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  if (process.client) {
    if (loggedIn.value) {
      reloadProducts()
    } else {
      loadingPDP.value = false
    }
  }
  nuxtApp.$eventbus.on('emit-warehouse-change', async (alternativePlantCode) => {
    const newProductData = await getProductBySkuNew(product?.value?.sku, nuxtApp.$globalization.getRegion(), i18n.locale.value, showInchValues.value, alternativePlantCode, PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW)
    product.value = newProductData
  })
  useGtm().trackEvent({
    event: 'view_item',
    ecommerce: {items: [{
      item_id: product?.value?.sku,
      item_name: product?.value?.locales[i18n.locale.value],
      item_brand: product?.value?.brandName,
      item_category: product?.value?.productDesign
    }]}
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
  nuxtApp.$eventbus.off('emit-warehouse-change')
})

</script>

<style lang="scss">
.product-page {
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: $color--white;
  padding-bottom: 0;

  .renderingMask {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  h1 {
    margin-top: .2rem;
  }

  .h5.product-page__headline {
    margin-bottom: 0;
    padding-top: 1rem;
  }

  &__brandInfo {
    position: relative;

    &__infoIcon {
      right: -.5rem;
      position: absolute !important;
    }

    &__logo {
      float: right;
      width: 15%;
      margin: .5rem 1rem;
    }
  }

  &__headline {
    white-space: normal;
    clear: none;
    word-wrap: break-word;
    display: flex;
  }

  @include breakpoint-down($md) {
    background-color: $color__content--background__secondary;

    .category-navigation {
      height: 3.6875rem;

      &__list {
        &__item a{
          min-height: 2.5625rem;
        }
      }
    }
  }

  .loading-indicator.loading--assets,
  .loading-indicator.loading--downloads {
    margin: 1rem auto;
    display: flex;
    align-self: center;
  }

  &__wrapper:nth-child(1) {
    order: 1;
  }

  &__column--white {
    background-color: $color__content--background__primary;
  }

  &__column--padding {
    padding: 0 1em;
  }

  &__below {
    padding-top: 2.5rem;
    padding-bottom: 14rem;

    @include breakpoint-down($md) {
      padding-top: 0;
      background-color: $color__content--background__primary;
    }
  }

  >.p-8 {
    padding: 0 2rem 1rem;
  }
}

.pdp-box {
  box-shadow: $default-figma-boxshadow;
  padding: 1rem;
  background-color: $color__white;
}

@include breakpoint-up($md) {
  .mobile {
    display: none;
  }
}

@include breakpoint-down($md) {
  .product-page {
    &__wrapper {
      padding: 4rem 0 14rem;
      flex-flow: column nowrap;
      justify-content: normal;
    }

    &__column {
      padding: 0 $default-margin-padding-width;

      &:last-child {
        padding-bottom: .5rem;
      }

      > div {
        background-color: $color__content--background__primary;
      }
    }

    &__column:first-child {
      padding: 0;
    }
  }

  .desktop {
    display: none;
  }

  .mobile {
    &__header {
      padding: 0 $default-margin-padding-width;
      position: fixed;
      width: calc(100% - 2rem);
      z-index: 7;
      background-color: $color__content--background__secondary;
      color: $color__text__search;

      &__section {
        height: 3.4375rem;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        text-decoration: none !important;
        color: $color__text__search;

        &--checked {
          color: $color__primary;
          border-bottom: solid .3em $color__primary;
        }
      }
    }

    &--blue {
      background-color: $color__content--background__secondary;
    }
  }
}

.app-vue {
  max-width: 90rem;
  margin: 0;
  background-color: $color__content--background__secondary;
  padding: 1.5rem;
  color: $color__text__default;

  .product-bar {
    width: 100%;
    height: auto;
    padding-bottom: 14rem;
    flex-direction: row;
    gap: 1.5rem;

    &.two-column {
      display: flex;

      .column-one {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        width: calc(50.4% - .75rem);
        min-width: 33rem;

        .pdp-box.product-bar__availability {
          display: none;
          min-width: 20rem;
          margin-bottom: 1.5rem;

          @include breakpoint-down($md) {
            min-width: auto;
          }
        }

        .product-bar__details {
          min-width: 33rem;
          margin-top: 0;

          @include breakpoint-down($md) {
            min-width: auto;
          }
        }

        .product-bar__dimensions {
          min-width: 33rem;
          margin-top: 0;

          @include breakpoint-down($md) {
            min-width: auto;
            padding: 1rem;
          }
        }

        @include breakpoint-down($md) {
          min-width: auto;
        }
      }

      .column-two {
        width: calc(49.6% - .75rem);
        min-width: 20rem;
      }

      @include breakpoint-down($between_md_lg) {
        flex-direction: column;
        gap: 0;

        .column-one {
          width: 100%;

          .pdp-box.product-bar__availability {
            display: flex;

            #newProductBuyBox {
              width: 100%;
            }
          }
        }

        .column-two {
          display: none;
        }
      }

      @include breakpoint-down($between_md_lg) {
        .product-bar {
          max-height: none;

          .product-bar__availability {
            width: calc(100% - 2rem);
            margin-bottom: 1.5rem;
            margin-left: 0;
          }
        }
      }
    }
  }

  @include breakpoint-down($md) {
    padding: $default-margin-padding-width $default-margin-padding-width 1.5rem;

    .product-bar {
      padding-bottom: 0;
    }
  }
}
</style>
